import React, { createContext, useContext, useEffect, useState } from 'react';
import { api, setAuth, clearAuth, getStoredUser, getAccessToken, setStoredUser } from '@/lib/api';

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  signup: async () => {},
  logout: () => {},
  refresh: async () => {},
  setUser: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!getAccessToken()) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      setStoredUser(data);
    } catch (e) {
      clearAuth();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, []);

  /**
   * Sign in flow with optional 2FA challenge.
   *
   * The backend returns one of two shapes for ``/auth/login``:
   *   1. ``{access_token, refresh_token, user}`` — straight sign-in success
   *   2. ``{mfa_required: true, mfa_token}`` — password OK but the user has
   *      TOTP enrolled and we need to exchange the short-lived ``mfa_token``
   *      + their 6-digit code at ``/auth/2fa/verify``.
   * To keep callers simple, we return either the user object (on success)
   * OR an object ``{mfaRequired: true, mfaToken}`` so the Login page can
   * branch into the code-entry screen.
   */
  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data && data.mfa_required) {
      return { mfaRequired: true, mfaToken: data.mfa_token };
    }
    setAuth(data);
    setUser(data.user);
    return data.user;
  };

  /**
   * Second step of the 2FA login: exchange (mfaToken, code) for real tokens.
   */
  const verifyMfa = async (mfaToken, code) => {
    const { data } = await api.post('/auth/2fa/verify', { mfa_token: mfaToken, code });
    setAuth(data);
    setUser(data.user);
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post('/auth/signup', payload);
    setAuth(data);
    setUser(data.user);
    return data.user;
  };

  const logout = (redirectTo = '/') => {
    // Hard redirect (instead of react-router's `nav`) so we get a fully clean
    // page-load with no stale React state from the protected page the user
    // just left. This eliminates the "Access denied" flicker that briefly
    // showed when a staff member signed out from a role-restricted page
    // (e.g. /admin, /inventory, /staff) before the new auth state propagated.
    clearAuth();
    setUser(null);
    if (typeof window !== 'undefined' && redirectTo) {
      window.location.replace(redirectTo);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refresh, verifyMfa, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
